import { brandApi } from "@/lib/axios";
import axios from "axios";

// ---------------------------------------------------------------------------
// Types (Sociable Search API response shapes, proxied via our backend)
// ---------------------------------------------------------------------------

export interface SociableSearchMetrics {
  likes: number;
  comments: number;
  views?: number;
  plays?: number;
  viewCount?: number;
  playCount?: number;
  videoViews?: number;
}

export interface SociableSearchResult {
  id: string;
  platform: string;
  url: string;
  caption: string;
  mediaUrl: string;
  creatorHandle: string;
  postedAt: string;
  metrics: SociableSearchMetrics;
  subject: string | null;
}

export interface SociableSearchResponse {
  requestId: string;
  query: string;
  message: string;
  suggestions: string[];
  totalCount: number;
  results: SociableSearchResult[];
}

export interface SociablePostRegion {
  code: string;
  confidence: number | null;
}

export interface SociablePostProduct {
  name: string;
  brand: string | null;
}

export interface SociablePostFormat {
  category: string;
  subtype: string;
}

export interface SociablePostAnalysis {
  subject: string | null;
  summary: string | null;
  language: string | null;
  regions: SociablePostRegion[];
  format: SociablePostFormat | null;
  topics: string[];
  brands: string[];
  products: SociablePostProduct[];
}

export interface SociablePostCreator {
  handle: string;
  name: string;
  followers: number;
  category: string;
  bio: string;
  isVerified: boolean;
}

export interface SociablePost {
  id: string;
  platform: string;
  url: string;
  caption: string;
  mediaUrl: string;
  creatorHandle: string;
  postedAt: string;
  metrics: SociableSearchMetrics;
}

export interface SociablePostDetailResponse {
  post: SociablePost;
  creator: SociablePostCreator;
  analysis: SociablePostAnalysis;
  transcript: string | null;
}

export type AiSearchMessageStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED";

export interface AiSearchCredits {
  planName: string;
  base: number;
  extended: number;
  /** Unexpired purchased pack credits still available (30-day validity). */
  purchased: number;
  used: number;
  monthlyUsed: number;
  monthlyRemaining: number;
  remaining: number;
  total: number;
  monthKey: string;
  pack: {
    credits: number;
    priceUsd: number;
    validityDays: number;
  };
}

export interface AiSearchConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    messages: number;
  };
}

export interface AiSearchMessage {
  id: string;
  conversationId: string;
  role: "USER" | "ASSISTANT";
  text: string | null;
  resultJson: SociableSearchResponse | null;
  status: AiSearchMessageStatus;
  errorCode: string | null;
  errorMessage: string | null;
  sociableRequestId: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AiSearchConversationDetail {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: AiSearchMessage[];
}

export interface StartSearchResponse {
  searchId: string;
  conversationId: string;
}

export interface SearchStatusResponse {
  status: AiSearchMessageStatus;
  result?: SociableSearchResponse;
  errorCode?: string;
  errorMessage?: string;
  elapsedMs: number;
}

/** Return the best-available view/play count from a metrics object, if any. */
export function getViewCount(metrics: SociableSearchMetrics): number | null {
  const extra = metrics as SociableSearchMetrics & Record<string, unknown>;
  const candidates = [
    metrics.views,
    metrics.plays,
    metrics.viewCount,
    metrics.playCount,
    metrics.videoViews,
    extra.views,
    extra.plays,
    extra.viewCount,
    extra.playCount,
    extra.videoViews,
    extra.impressions,
  ];

  for (const value of candidates) {
    if (typeof value === "number" && !Number.isNaN(value)) return value;
  }
  return null;
}

export function getAiSearchErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; code?: string }
      | undefined;

    if (typeof data?.message === "string") return data.message;
    if (Array.isArray(data?.message)) return data.message.join(", ");

    if (err.response?.status === 402) {
      return "You have used all your Social searches for this month. Buy a search pack to keep going.";
    }
    if (err.response?.status === 409) {
      return "A search is already running. Please wait for it to finish.";
    }
    if (err.response?.status === 429) {
      return "Too many requests. Please wait a bit and try again.";
    }
    return err.message || "Something went wrong while searching.";
  }
  return "Something went wrong while searching.";
}

export const aiSearchService = {
  async getCredits(): Promise<AiSearchCredits> {
    const response = await brandApi.get<AiSearchCredits>("/ai-search/credits");
    return response.data;
  },

  /** Buy a $50 overage pack of 50 searches, billed to the Shopify subscription. */
  async purchasePack(): Promise<AiSearchCredits> {
    const response = await brandApi.post<AiSearchCredits>(
      "/ai-search/packs/purchase"
    );
    return response.data;
  },

  async listConversations(): Promise<AiSearchConversationSummary[]> {
    const response = await brandApi.get<AiSearchConversationSummary[]>(
      "/ai-search/conversations"
    );
    return response.data;
  },

  async getConversation(id: string): Promise<AiSearchConversationDetail> {
    const response = await brandApi.get<AiSearchConversationDetail>(
      `/ai-search/conversations/${id}`
    );
    return response.data;
  },

  async deleteConversation(id: string): Promise<void> {
    await brandApi.delete(`/ai-search/conversations/${id}`);
  },

  async startSearch(
    query: string,
    conversationId?: string | null
  ): Promise<StartSearchResponse> {
    const response = await brandApi.post<StartSearchResponse>(
      "/ai-search/searches",
      {
        query,
        ...(conversationId ? { conversationId } : {}),
      }
    );
    return response.data;
  },

  async getSearchStatus(searchId: string): Promise<SearchStatusResponse> {
    const response = await brandApi.get<SearchStatusResponse>(
      `/ai-search/searches/${searchId}`
    );
    return response.data;
  },

  async getPost(postId: string): Promise<SociablePostDetailResponse> {
    const response = await brandApi.get<SociablePostDetailResponse>(
      `/ai-search/posts/${encodeURIComponent(postId)}`
    );
    return response.data;
  },
};
